import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { formatPriceUSD } from "../format";
import {
  CaregiverCertification,
  FlexiblePatternVisitType,
  Geo,
  OnboardingVisitInstanceId,
  PatientGender,
  PublicOnboardingVisitDetails,
  PublicVisitBroadcastDetails,
  SingleFlexible,
  VisitInstanceId,
} from "../messages";
import { STRINGS } from "../strings";
import { VisitType } from "../system/AppState/AppVisitsState";
import { globalize, translate } from "../translate";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { HashSet } from "../utils/HashSet";
import {
  CALENDAR_ICON,
  CERTIFICATION_ICON,
  DOLLAR_PAPER,
  LANGUAGE_ICON,
  MAP_MARKER_ICON,
} from "../utils/svgs_new";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableVisitDistance from "./AvailableVisitDistance";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppList, AppListItem } from "./Generic/AppList";
import Flex from "./Generic/Flex";
import { If } from "./Generic/If";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";
import PatientInformationAppListItem from "./PatientInformationAppListItem";

interface Props {
  patientName: string;
  patientAge: number | null;
  patientGender: PatientGender;
  patientLanguages: string[];
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails;
  caregiverLocation: Geo;
  isMedflytCaregiver: boolean;
  checkedVisits: HashSet<VisitInstanceId | OnboardingVisitInstanceId>;
  checkedPatterns: HashSet<string>;
  requestedVisitBatch: boolean;
  areAllShiftsChecked: boolean;
  visitType: VisitType;
  visitDuration?: string;
  onClickLocation: () => void;
  onCheckVisit:
    | ((visitData: CheckedVisitData) => void)
    | ((visitData: CheckedOnboardingVisitData) => void);
}

const AvailableVisitDetails = (props: Props) => {
  const displayPrice =
    props.broadcastDetails.price !== null
      ? formatPriceUSD(+props.broadcastDetails.price) + " per hour"
      : undefined;

  const isMedflytCaregiverWithCertificationHHAOrPCA =
    props.broadcastDetails.certifications.some((c) =>
      [CaregiverCertification.wrap("HHA"), CaregiverCertification.wrap("PCA")].includes(c)
    ) && props.isMedflytCaregiver;

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>{translate(STRINGS.VISIT_DETAILS)}</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <AppList>
          <AppListItem
            leading={<AppIcon icon={CALENDAR_ICON} fill="primary" />}
            title={formatVisitDates(props.broadcastDetails)}
            extra={
              <AppListItem
                title={
                  props.visitDuration !== undefined &&
                  props.broadcastDetails.type === "FlexiblePattern" ? (
                    <VisitDurationDetails
                      visitDuration={props.visitDuration}
                      flexibleVisitType={props.broadcastDetails.flexibleVisitParams.type}
                    />
                  ) : (
                    translate(STRINGS.MORE_DETAILS_BELOW)
                  )
                }
                soft={true}
              />
            }
          />
          {(!isMedflytCaregiverWithCertificationHHAOrPCA || !props.isMedflytCaregiver) && (
            <AppListItem
              leading={<AppIcon icon={CERTIFICATION_ICON} fill="primary" />}
              title={props.broadcastDetails.certifications.join(", ")}
            />
          )}
          {!props.isMedflytCaregiver || props.broadcastDetails.nearLocationDisplay === null ? (
            <AppListItem
              leading={<AppIcon icon={MAP_MARKER_ICON} fill="primary" />}
              title={getLocationDisplayLocation(props.broadcastDetails.location)}
              extra={
                <AppListItem
                  title={
                    <AvailableVisitDistance
                      origin={props.caregiverLocation}
                      destination={props.broadcastDetails.location.location}
                    />
                  }
                  soft={true}
                />
              }
              onClick={props.onClickLocation}
            />
          ) : (
            <AppListItem
              leading={<AppIcon icon={MAP_MARKER_ICON} fill="primary" />}
              title={`${translate(STRINGS.NEAR)} ${props.broadcastDetails.nearLocationDisplay}`}
              extra={
                <AppButton onClick={props.onClickLocation} size="small">
                  {translate(STRINGS.OPEN_MAPS)}
                </AppButton>
              }
            />
          )}

          <PatientInformationAppListItem
            patientAge={props.patientAge}
            patientGender={props.patientGender}
            patientName={props.patientName}
            hasAccessToPatient={false}
          />

          {displayPrice !== undefined && (
            <AppListItem
              leading={<AppIcon icon={DOLLAR_PAPER} fill="primary" />}
              title={displayPrice}
            />
          )}

          <If condition={props.patientLanguages.length > 0}>
            <AppListItem
              leading={<AppIcon icon={LANGUAGE_ICON} fill="primary" />}
              title={props.patientLanguages.join(", ")}
            />
          </If>
        </AppList>
      </AppCardContent>
    </AppCard>
  );
};

const VisitDurationDetails = (props: {
  visitDuration: string;
  flexibleVisitType: FlexiblePatternVisitType | SingleFlexible["type"];
}) => {
  let youCanChooseText = translate(STRINGS.YOU_CAN_CHOOSE_WHICH_HOURS_FLEXIBLE_VISIT);

  switch (props.flexibleVisitType) {
    case "PermanentFixedHoursFlexibleDays":
      youCanChooseText = translate(STRINGS.YOU_CAN_CHOOSE_WHICH_DAYS_FLEXIBLE_VISIT);
      break;
    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursAndDaysPerWeek":
      youCanChooseText = translate(STRINGS.YOU_CAN_CHOOSE_WHICH_DAYS_AND_HOURS_FLEXIBLE_VISIT);
      break;
    case "PermanentFlexibleHoursFixedDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "SingleFlexible":
      youCanChooseText = translate(STRINGS.YOU_CAN_CHOOSE_WHICH_HOURS_FLEXIBLE_VISIT);
      break;
  }

  return (
    <Flex gap={4} direction="column">
      <div>{props.visitDuration}</div>
      <div>{youCanChooseText}</div>
    </Flex>
  );
};

function formVisitDate(date: LocalDateTime) {
  return globalize().formatLocalDate(date.toLocalDate(), { date: "short", skeleton: "Md" });
}

function formatVisitDates(p: { startTimeLocal: LocalDateTime; endTimeLocal: LocalDateTime }) {
  if (p.startTimeLocal.equals(p.endTimeLocal)) {
    return formVisitDate(p.startTimeLocal);
  }

  return `${formVisitDate(p.startTimeLocal)} - ${formVisitDate(p.endTimeLocal)}`;
}

export default AvailableVisitDetails;
